import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { PlaylistVideo } from '@/store/playlist/types';
import { Play } from 'lucide-react';
import { TemplateLightHeader } from './TemplateLightHeader';
import { TemplateLightEpisodeCard } from './TemplateLightEpisodeCard';
import { TemplateLightFooter } from './TemplateLightFooter';
import hostVslThumbnail from '@/assets/hostvsl-thumbnail-placeholder.jpg';
import logoHostVslWhite from '@/assets/logo-hostvsl-white.png';

interface TemplatePandaPreviewProps {
  videos: PlaylistVideo[];
  playlistName?: string;
  publicTitle?: string;
  favicon?: string;
  description?: string;
}

export function TemplatePandaPreview({ 
  videos, 
  playlistName = 'Curso de Exemplo',
  publicTitle,
  favicon,
  description,
}: TemplatePandaPreviewProps) {
  useLanguage();
  const [selectedVideo, setSelectedVideo] = useState(0);
  const [watchedEpisodes] = useState<Set<number>>(new Set());
  const [showNextOverlay, setShowNextOverlay] = useState(false);
  const [countdown, setCountdown] = useState(5);
  
  // Estado de progresso por vídeo (mock para preview)
  const [videoProgress] = useState<Record<number, number>>({
    0: 100,  // Vídeo 1 - 100% assistido
    1: 65,   // Vídeo 2 - 65% assistido  
    2: 30,   // Vídeo 3 - 30% assistido
    3: 0,    // Vídeo 4 - não iniciado
    4: 15,   // Vídeo 5 - 15% assistido
    5: 0,    // Vídeo 6 - não iniciado
    6: 0,    // Vídeo 7 - não iniciado
  });

  // Variáveis para próximo vídeo
  const hasNextVideo = selectedVideo < videos.length - 1;
  const nextVideo = hasNextVideo ? videos[selectedVideo + 1] : null;
  const nextVideoIndex = selectedVideo + 1;

  // Avançar para próximo vídeo
  const goToNextVideo = () => {
    if (selectedVideo < videos.length - 1) {
      setSelectedVideo(prev => prev + 1);
    }
    setShowNextOverlay(false);
    setCountdown(5);
  };

  // Handler quando vídeo termina
  const handleVideoEnded = () => {
    if (selectedVideo < videos.length - 1) {
      setShowNextOverlay(true);
    }
  };

  // Cancelar autoplay
  const cancelAutoplay = () => {
    setShowNextOverlay(false);
    setCountdown(5);
  };

  // Countdown timer
  useEffect(() => {
    if (!showNextOverlay) return;
    
    if (countdown <= 0) {
      goToNextVideo();
      return;
    }
    
    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [showNextOverlay, countdown]);

  // Descrição de exemplo para preview mock
  const displayDescription = description || translateInterfaceText("Aprenda as melhores estratégias e técnicas neste curso completo. Conteúdo exclusivo desenvolvido para transformar seus resultados.");
  
  // Descrição de exemplo para o primeiro vídeo (mock para preview)
  const defaultEpisodeDescription = selectedVideo === 0 
    ? 'Nesta primeira aula, você vai aprender os conceitos fundamentais que serão a base para todo o curso. Prepare-se para uma jornada de transformação!'
    : undefined;

  // Descrição do episódio selecionado
  const selectedEpisodeDescription = videos[selectedVideo]?.episodeDescription || defaultEpisodeDescription;

  return (
    <div data-theme-isolated className="h-screen flex flex-col bg-[#FAFBFC] overflow-hidden">
      {/* Header Premium 64px */}
      <TemplateLightHeader playlistName={playlistName} publicTitle={publicTitle} favicon={favicon} />

      {/* Container Principal - Desktop */}
      <div className="hidden lg:flex flex-1 overflow-hidden">
        <div className="w-full max-w-[1400px] mx-auto px-10 py-6 flex gap-6 h-full">
          {/* Player Container - 65% */}
          <div className="flex-[0_0_65%] max-w-[900px]">
            {/* Player */}
            <div 
              className="relative aspect-video bg-gray-900 rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
            >
              <video
                key={selectedVideo}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                src={`https://example.com/videos/${videos[selectedVideo]?.videoId}.mp4`}
                onEnded={handleVideoEnded}
              >
                {translateInterfaceText("Seu navegador não suporta vídeos.")}</video>

              {/* Overlay de Próximo Vídeo */}
              {showNextOverlay && nextVideo && (
                <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                  <p className="text-white/60 text-sm mb-2">{translateInterfaceText("Próximo episódio em")}</p>
                  
                  {/* Countdown circular */}
                  <div className="relative w-20 h-20 mb-4">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" stroke="white" strokeWidth="3" fill="none" opacity="0.2" />
                      <circle
                        cx="50" cy="50" r="45"
                        stroke="#3B82F6" strokeWidth="3" fill="none"
                        strokeDasharray={`${2 * Math.PI * 45}`}
                        strokeDashoffset={`${2 * Math.PI * 45 * (1 - countdown / 5)}`}
                        className="transition-all duration-1000 ease-linear"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-white text-3xl font-bold">
                      {countdown}
                    </span>
                  </div>
                  
                  {/* Info do próximo vídeo */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-24 h-14 bg-[#0B1423] rounded-lg flex items-center justify-center overflow-hidden">
                      <img src={logoHostVslWhite} alt="" className="w-12 opacity-60" />
                    </div>
                    <div className="text-left">
                      <p className="text-white/60 text-xs">{translateInterfaceText("Episódio")}{" "}{nextVideoIndex + 1}</p>
                      <p className="text-white font-semibold text-sm max-w-[200px] truncate">
                        {nextVideo.customName || nextVideo.episodeTitle}
                      </p>
                    </div>
                  </div>
                  
                  {/* Botões */}
                  <div className="flex gap-3">
                    <button
                      onClick={cancelAutoplay}
                      className="px-6 py-2.5 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                    >
                      {translateInterfaceText("Cancelar")}</button>
                    <button
                      onClick={goToNextVideo}
                      className="px-6 py-2.5 rounded-full bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors"
                    >
                      {translateInterfaceText("Reproduzir agora")}</button>
                  </div>
                </div>
              )}
            </div>

            {/* Descrição do Vídeo */}
            {selectedEpisodeDescription && (
              <div className="mt-6 flex items-start justify-between gap-6">
                <div className="flex-1">
                  <p className="text-[14px] text-[#4B5563] max-w-2xl leading-relaxed">
                    {selectedEpisodeDescription}
                  </p>
                </div>
                <button className="px-5 py-2.5 bg-[#3B82F6] text-white text-[14px] font-medium rounded-lg hover:bg-[#2563EB] transition-colors flex-shrink-0">
                  {translateInterfaceText("Criar minha Playlist")}</button>
              </div>
            )}
          </div>

          {/* Sidebar - Alinhada exatamente com o player */}
          <aside 
            className="flex flex-col"
            style={{ 
              minWidth: '360px',
              maxWidth: '390px',
              width: '360px',
            }}
          >
            <div 
              className="bg-white flex flex-col overflow-hidden"
              style={{
                borderRadius: '14px',
                boxShadow: '0px 4px 22px rgba(0, 0, 0, 0.06)',
                height: 'auto',
                minHeight: '750px',
                maxHeight: 'calc(100vh - 180px)',
              }}
            >
              {/* Header Fixo */}
              <div className="flex-shrink-0 p-5 border-b border-[#F3F4F6]">
                <h2 className="text-[17px] font-semibold text-[#1A1F36] mb-2">
                  {publicTitle || playlistName}
                </h2>
                {displayDescription && (
                  <p className="text-[13px] text-[#6B7280] leading-relaxed">
                    {displayDescription}
                  </p>
                )}
              </div>

              {/* Lista de Episódios - Com scroll */}
            <div 
              className="flex-1 overflow-y-auto sidebar-scroll"
              style={{ 
                padding: '12px 0 4px 0',
                maxHeight: '580px'
              }}
            >
                {videos.map((video, index) => (
                  <TemplateLightEpisodeCard
                    key={video.id}
                    index={index}
                    title={video.customName || video.episodeTitle || translateInterfaceText("Vídeo {value0}", { value0: (index + 1) })}
                    duration={`${Math.floor(Math.random() * 10 + 3)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`}
                    thumbnail={video.customThumbnail || hostVslThumbnail}
                    isPlaying={selectedVideo === index}
                    isWatched={videoProgress[index] === 100}
                    progress={videoProgress[index] || 0}
                    onClick={() => setSelectedVideo(index)}
                  />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Layout Mobile/Tablet */}
      <div className="lg:hidden flex-1 overflow-y-auto">
        {/* Player */}
        <div className="p-4">
          <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
            <video
              key={selectedVideo}
              className="w-full h-full object-contain"
              controls
              autoPlay
              playsInline
              src={`https://example.com/videos/${videos[selectedVideo]?.videoId}.mp4`}
              onEnded={handleVideoEnded}
            >
              {translateInterfaceText("Seu navegador não suporta vídeos.")}</video>

            {/* Overlay de Próximo Vídeo Mobile */}
            {showNextOverlay && nextVideo && (
              <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                <p className="text-white/60 text-xs mb-2">{translateInterfaceText("Próximo episódio em")}</p>
                
                {/* Countdown circular mobile */}
                <div className="relative w-16 h-16 mb-3">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" stroke="white" strokeWidth="3" fill="none" opacity="0.2" />
                    <circle
                      cx="50" cy="50" r="45"
                      stroke="#3B82F6" strokeWidth="3" fill="none"
                      strokeDasharray={`${2 * Math.PI * 45}`}
                      strokeDashoffset={`${2 * Math.PI * 45 * (1 - countdown / 5)}`}
                      className="transition-all duration-1000 ease-linear"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-white text-2xl font-bold">
                    {countdown}
                  </span>
                </div>
                
                {/* Info do próximo vídeo mobile */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-20 h-12 bg-[#0B1423] rounded-lg flex items-center justify-center overflow-hidden">
                    <img src={logoHostVslWhite} alt="" className="w-10 opacity-60" />
                  </div>
                  <div className="text-left">
                    <p className="text-white/60 text-[10px]">{translateInterfaceText("Episódio")}{" "}{nextVideoIndex + 1}</p>
                    <p className="text-white font-semibold text-xs max-w-[150px] truncate">
                      {nextVideo.customName || nextVideo.episodeTitle}
                    </p>
                  </div>
                </div>
                
                {/* Botões mobile */}
                <div className="flex gap-2">
                  <button
                    onClick={cancelAutoplay}
                    className="px-5 py-2 rounded-full bg-white/10 text-white text-xs font-medium hover:bg-white/20 transition-colors"
                  >
                    {translateInterfaceText("Cancelar")}</button>
                  <button
                    onClick={goToNextVideo}
                    className="px-5 py-2 rounded-full bg-[#3B82F6] text-white text-xs font-medium hover:bg-[#2563EB] transition-colors"
                  >
                    {translateInterfaceText("Reproduzir agora")}</button>
                </div>
              </div>
            )}
          </div>

          {/* Descrição Mobile */}
          {selectedEpisodeDescription && (
            <div className="mt-4">
              <p className="text-[13px] text-[#4B5563] mb-3 leading-relaxed">
                {selectedEpisodeDescription}
              </p>
              <button className="w-full px-6 py-2.5 bg-[#3B82F6] text-white text-[14px] font-medium rounded-lg hover:bg-[#2563EB] transition-colors">
                {translateInterfaceText("Criar minha Playlist")}</button>
            </div>
          )}
        </div>

        {/* Título da Playlist Mobile */}
        <div className="px-4 pb-3 pt-2">
          <h3 className="text-[16px] font-semibold text-[#1A1F36] mb-1">
            {publicTitle || playlistName}
          </h3>
          {displayDescription && (
            <p className="text-[12px] text-[#3B5998] bg-[#EEF2FF] px-2.5 py-1 rounded-lg leading-relaxed">
              {displayDescription}
            </p>
          )}
        </div>

        {/* Lista de Episódios Mobile */}
        <div className="px-2 pb-4">
          {videos.map((video, index) => (
            <TemplateLightEpisodeCard
              key={video.id}
              index={index}
              title={video.customName || video.episodeTitle || translateInterfaceText("Vídeo {value0}", { value0: (index + 1) })}
              duration={`${Math.floor(Math.random() * 10 + 3)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`}
              thumbnail={video.customThumbnail || hostVslThumbnail}
              isPlaying={selectedVideo === index}
              isWatched={videoProgress[index] === 100}
              progress={videoProgress[index] || 0}
              onClick={() => setSelectedVideo(index)}
            />
          ))}
        </div>
      </div>

      {/* Footer Premium */}
      <TemplateLightFooter 
        showWhatsapp={true}
        whatsappNumber="11999999999"
        whatsappMessage="Olá! Vim pela playlist"
      />

      {/* CSS para scrollbar Panda */}
      <style>{`
        .sidebar-scroll::-webkit-scrollbar {
          width: 5px;
        }
        .sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 100px;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
      `}</style>
    </div>
  );
}
